import { CognitoUser } from "amazon-cognito-identity-js";
import {
    ChangePassword,
    ConfirmForgot,
    ConfirmSignUp,
    Login,
    SignUp,
} from "generated/models";

// New unified types and factory
import { AxiosError } from "axios";
import { getAccountInstance } from "generated/factory";
import AxiosInstance from "service/axiosInstance";
import {
    createErrorResponseStandard,
    createResponseStandard,
} from "utils/functions";
import { AuthenticationFactory } from "./authentication/authenticationFactory";
import {
    AuthMFAResponse,
    AuthResponse,
    IAuthenticationStrategy,
    MFASubmissionParams,
    TokenResponse,
} from "./authentication/authenticationTypes";
// Re-export types for backward compatibility
export type { AuthMFAResponse, AuthResponse, TokenResponse };
export type LoginSession = CognitoUser | string;

// New unified authentication service
export class AuthenticationService implements IAuthenticationStrategy {
    private readonly strategy: IAuthenticationStrategy;
    private readonly accountClient = getAccountInstance(AxiosInstance);

    constructor() {
        this.strategy = AuthenticationFactory.getAuthenticationService();
    }

    // New unified methods
    async login(credentials: Login): Promise<AuthResponse> {
        return await this.strategy.login(credentials);
    }

    // Method overloads for submitMFACode
    async submitMFACode(params: MFASubmissionParams): Promise<AuthResponse>;
    async submitMFACode(
        session: CognitoUser | string,
        mfaCode: string
    ): Promise<AuthResponse>;
    async submitMFACode(
        paramsOrSession: MFASubmissionParams | CognitoUser | string,
        mfaCode?: string
    ): Promise<AuthResponse> {
        // Handle both old and new signatures
        if (mfaCode !== undefined) {
            // Legacy API call: submitMFACode(session, mfaCode)

            // paramsOrSession must be either a valid CognitoUser or string
            if (
                typeof paramsOrSession !== "string" &&
                !(paramsOrSession instanceof CognitoUser)
            ) {
                throw new TypeError(
                    "Invalid parameters for submitMFACode. Use either submitMFACode(session, mfaCode) or submitMFACode({ session, mfaCode, challengeName })"
                );
            }
            return await this.strategy.submitMFACode({
                session: paramsOrSession,
                mfaCode,
            });

        } else if (
            typeof paramsOrSession === "object" &&
            paramsOrSession !== null &&
            "mfaCode" in paramsOrSession
        ) {
            // New unified API call: submitMFACode({ session, mfaCode, challengeName })
            return await this.strategy.submitMFACode(paramsOrSession);
        } else {
            throw new Error(
                "Invalid parameters for submitMFACode. Use either submitMFACode(session, mfaCode) or submitMFACode({ session, mfaCode, challengeName })"
            );
        }
    }

    async sendForgotPasswordEmail(email: string) {
        try {
            const response = await this.accountClient.forgotPassword({
                email,
            });
            return createResponseStandard(response);
        } catch (error) {
            return createErrorResponseStandard(error as AxiosError);
        }
    }

    async changePassword(body: ConfirmForgot) {
        try {
            const response =
                await this.accountClient.confirmForgotPasswordWithCode(body);
            return createResponseStandard(response);
        } catch (error) {
            return createErrorResponseStandard(error as AxiosError);
        }
    }

    async signUp(body: SignUp) {
        try {
            const response = await this.accountClient.signUp(body);
            return createResponseStandard(response);
        } catch (error) {
            return createErrorResponseStandard(error as AxiosError);
        }
    }

    async submitSignUpVerificationCode(body: ConfirmSignUp) {
        try {
            const response = await this.accountClient.confirmSighUpWithCode(
                body
            );
            return createResponseStandard(response);
        } catch (error) {
            return createErrorResponseStandard(error as AxiosError);
        }
    }

    async changeProfilePassword(body: ChangePassword) {
        try {
            const response = await this.accountClient.changePassword(body);
            return createResponseStandard(response);
        } catch (error) {
            return createErrorResponseStandard(error as AxiosError);
        }
    }

    async resendConfirmationCode(email: string) {
        try {
            const response = await this.accountClient.reSendConfirmationCode({
                email: email,
            });
            return createResponseStandard(response);
        } catch (error) {
            return createErrorResponseStandard(error as AxiosError);
        }
    }

    // Utility methods
    getCurrentProvider() {
        return AuthenticationFactory.getCurrentProvider();
    }
}

const authService = new AuthenticationService();
export default authService;
